import { AssetImage } from "./AssetImage.js";
import { sanitizeViewSpec, type ViewSpec, type ViewWidget } from "@roqeet/core";
import { useT } from "../../i18n/use-t.js";

/**
 * UI 프리미티브 렌더러 — 결정 #27. 게임 모듈이 선언한 ViewSpec의 위젯들을
 * React로 그린다. 임의 코드 없이 데이터 선언만으로 게임 UI를 구성하게 한다.
 *
 * 보안(결정 #17): core의 sanitizeViewSpec으로 화이트리스트 위젯만 통과시킨
 * 뒤 렌더한다 — 알 수 없는 종류는 무시.
 *
 * 콜백: 선택지/투표는 사용자 입력을 구동한다. 선택하면 onChoice(텍스트)가 전송
 * 경로로 연결된다(채팅방에서 주입).
 */
export function ViewSpecRenderer({
  spec,
  onChoice,
  onVote,
  busy,
}: {
  spec: ViewSpec;
  onChoice?: (text: string) => void;
  onVote?: (candidateId: string) => void;
  busy?: boolean;
}) {
  const safe = sanitizeViewSpec(spec);
  if (safe.widgets.length === 0) return null;
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-2 px-1 pb-2">
      {safe.widgets.map((w, i) => (
        <Widget key={i} widget={w} onChoice={onChoice} onVote={onVote} busy={busy} />
      ))}
    </div>
  );
}

/** HUD(헤더/사이드 고정 영역)용 렌더러 — 같은 위젯 집합, 표시 위주. */
export function HudRenderer({ spec }: { spec: ViewSpec }) {
  const safe = sanitizeViewSpec(spec);
  if (!safe.hud || safe.hud.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
      {safe.hud.map((w, i) => (
        <Widget key={i} widget={w} compact />
      ))}
    </div>
  );
}

function Widget({
  widget,
  onChoice,
  onVote,
  busy,
  compact,
}: {
  widget: ViewWidget;
  onChoice?: (text: string) => void;
  onVote?: (candidateId: string) => void;
  busy?: boolean;
  compact?: boolean;
}) {
  const { t } = useT();
  switch (widget.kind) {
    case "choices":
      return (
        <div className="flex flex-col gap-1.5">
          {widget.choices.map((c, i) => (
            <button
              key={i}
              className="rounded-lg border border-line bg-bg-soft px-3 py-2 text-left text-sm text-ink hover:border-accent hover:bg-bg-raised disabled:opacity-50"
              disabled={busy || widget.disabled}
              onClick={() => onChoice?.(c)}
            >
              {c}
            </button>
          ))}
        </div>
      );

    case "stat-bar": {
      const pct = widget.max > 0 ? Math.max(0, Math.min(1, widget.value / widget.max)) : 0;
      const toneColor =
        widget.tone === "hp"
          ? "bg-red-500"
          : widget.tone === "mp"
            ? "bg-blue-500"
            : widget.tone === "affection"
              ? "bg-pink-500"
              : "bg-accent";
      return (
        <div className={compact ? "min-w-[120px]" : ""}>
          <div className="mb-0.5 flex justify-between text-xs text-ink-soft">
            <span>{widget.label}</span>
            <span className="tabular-nums">
              {widget.value}/{widget.max}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-bg-raised">
            <div className={`h-full ${toneColor}`} style={{ width: `${pct * 100}%` }} />
          </div>
        </div>
      );
    }

    case "stat-panel":
      return (
        <div className="rounded-lg border border-line bg-bg-soft p-2">
          {widget.title && (
            <div className="mb-1 text-xs font-semibold text-ink-faint">{widget.title}</div>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {widget.stats.map((s, i) => (
              <span key={i}>
                <span className="text-ink-faint">{s.label}:</span>{" "}
                <span className="text-ink tabular-nums">{s.value}</span>
              </span>
            ))}
          </div>
        </div>
      );

    case "inventory":
      return (
        <div className="rounded-lg border border-line bg-bg-soft p-2">
          {widget.title && (
            <div className="mb-1 text-xs font-semibold text-ink-faint">{widget.title}</div>
          )}
          {widget.items.length === 0 ? (
            <div className="text-xs text-ink-faint">{t("gameWidget.empty")}</div>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {widget.items.map((it, i) => (
                <li
                  key={i}
                  className="flex items-center gap-1 rounded-md border border-line bg-bg-raised px-2 py-1 text-xs text-ink"
                  title={it.name}
                >
                  {it.assetId ? (
                    <AssetImage assetId={it.assetId} alt={it.name} className="h-4 w-4 rounded" />
                  ) : it.icon ? (
                    <span>{it.icon}</span>
                  ) : null}
                  <span className="truncate">{it.name}</span>
                  {it.qty != null && it.qty > 1 && (
                    <span className="text-ink-faint">×{it.qty}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      );

    case "sprite-stage":
      return (
        <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-line bg-bg-raised">
          {widget.backgroundAssetId && (
            <AssetImage
              assetId={widget.backgroundAssetId}
              alt={t("gameWidget.backgroundAlt")}
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          <div className="absolute inset-0 flex items-end justify-around">
            {widget.sprites.map((s, i) => (
              <div
                key={i}
                className={[
                  "h-[85%]",
                  s.position === "left"
                    ? "self-end mr-auto"
                    : s.position === "right"
                      ? "self-end ml-auto"
                      : "",
                ].join(" ")}
              >
                {s.assetId && (
                  <AssetImage
                    assetId={s.assetId}
                    alt={s.emotion ?? t("gameWidget.characterAlt")}
                    className="h-full w-auto object-contain"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      );

    case "dice":
      return (
        <div
          className={[
            "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm",
            widget.success === true
              ? "border-green-700 bg-green-950/30 text-green-300"
              : widget.success === false
                ? "border-red-800 bg-red-950/30 text-red-300"
                : "border-line bg-bg-soft text-ink",
          ].join(" ")}
        >
          <span aria-hidden>🎲</span>
          <span className="font-mono">{widget.notation}</span>
          <span className="font-semibold tabular-nums">→ {widget.result}</span>
          {widget.success != null && (
            <span>{widget.success ? t("gameWidget.success") : t("gameWidget.failure")}</span>
          )}
        </div>
      );

    case "vote":
      return (
        <div className="rounded-lg border border-line bg-bg-soft p-2">
          {widget.title && (
            <div className="mb-1 text-xs font-semibold text-ink-faint">{widget.title}</div>
          )}
          <div className="flex flex-col gap-1.5">
            {widget.candidates.map((c) => (
              <button
                key={c.id}
                className="flex items-center justify-between rounded-lg border border-line bg-bg-raised px-3 py-2 text-left text-sm text-ink hover:border-accent disabled:opacity-50"
                disabled={busy || widget.voted}
                onClick={() => onVote?.(c.id)}
              >
                <span>{c.label}</span>
                {c.votes != null && (
                  <span className="text-ink-faint tabular-nums">
                    {t("gameWidget.votes", { count: c.votes })}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      );

    case "notice": {
      const tone =
        widget.tone === "success"
          ? "border-green-700 bg-green-950/30 text-green-300"
          : widget.tone === "warning"
            ? "border-yellow-700 bg-yellow-950/30 text-yellow-300"
            : widget.tone === "danger"
              ? "border-red-800 bg-red-950/30 text-red-300"
              : "border-line bg-bg-soft text-ink-soft";
      return <div className={`rounded-lg border px-3 py-2 text-xs ${tone}`}>{widget.text}</div>;
    }

    default:
      return null;
  }
}
